import { useState, useEffect, useCallback, useRef } from 'react';
import notificationSignalRService from '../../Services/notificationSignalRService';
import { useAuth0 } from '@auth0/auth0-react';

const useNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [notificationError, setNotificationError] = useState(null);
    const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
    const notificationAudioRef = useRef(null);
    const lastNotificationIdRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        notificationAudioRef.current = new Audio('/notification.mp3');

        const unlockAudio = () => {
            const audio = notificationAudioRef.current;
            if (!audio) {
                return;
            }

            audio.play()
                .then(() => {
                    audio.pause();
                    audio.currentTime = 0;
                })
                .catch(() => {});
        };

        document.addEventListener('click', unlockAudio, { capture: true, once: true });
        document.addEventListener('keydown', unlockAudio, { capture: true, once: true });

        return () => {
            document.removeEventListener('click', unlockAudio, { capture: true });
            document.removeEventListener('keydown', unlockAudio, { capture: true });
        };
    }, []);

    const playNotificationSound = useCallback(() => {
        const audio = notificationAudioRef.current ?? new Audio('/notification.mp3');
        if (!notificationAudioRef.current) {
            notificationAudioRef.current = audio;
        }
        audio.currentTime = 0;
        audio.play().catch(() => {});
    }, []);

    // Use `useCallback` to ensure the function has a stable reference
    const fetchNotifications = useCallback(async () => {
        setIsLoadingNotifications(true);
        setNotificationError(null);

        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                }
            });
            const response = await fetch(`${apiUrl}/api/notifications`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Notification request failed (${response.status})`);
            }
            const data = await response.json();
            const notificationList = Array.isArray(data) ? data : [];

            const sortedNotifications = notificationList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setNotifications(sortedNotifications);
            return sortedNotifications[0]?.id ?? null;
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
            setNotificationError("Notifications could not be loaded. Please try again.");
            return null;
        } finally {
            setIsLoadingNotifications(false);
        }
    }, [apiUrl, getAccessTokenSilently]);

    useEffect(() => {
        if (!isAuthenticated) {
            return undefined;
        }

        let isMounted = true;
        const unsubscribeState = notificationSignalRService.onStateChange((state) => {
            if (isMounted) setIsConnected(state === 'connected');
        });

        const handleNotification = async () => {
            const previousNotificationId = lastNotificationIdRef.current;
            const latestNotificationId = await fetchNotifications();

            if (latestNotificationId && latestNotificationId !== previousNotificationId) {
                playNotificationSound();
            }

            lastNotificationIdRef.current = latestNotificationId ?? previousNotificationId;
        };

        const ensureNotificationConnection = async () => {
            try {
                if (!notificationSignalRService.connection) {
                    await notificationSignalRService.start(() => getAccessTokenSilently({
                        authorizationParams: {
                            audience: process.env.REACT_APP_API_AUDIENCE,
                        },
                    }));
                }

                if (!isMounted) {
                    return;
                }

                notificationSignalRService.connection?.on("ReceiveNotification", handleNotification);
                const latestNotificationId = await fetchNotifications();
                lastNotificationIdRef.current = latestNotificationId;
            } catch (error) {
                console.error("Failed to initialize notifications connection:", error);
                if (isMounted) {
                    setIsConnected(false);
                }
            }
        };

        ensureNotificationConnection();

        return () => {
            isMounted = false;
            unsubscribeState();
            notificationSignalRService.connection?.off("ReceiveNotification", handleNotification);
        };
    }, [fetchNotifications, getAccessTokenSilently, isAuthenticated, playNotificationSound]);

    const markAsRead = async (id) => {
        const token = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_API_AUDIENCE,
            }
        });
        const response = await fetch(`${apiUrl}/api/notifications/${id}/read`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: 'PUT' });
        if (!response.ok) {
            throw new Error(`Mark notification read failed (${response.status})`);
        }
        await fetchNotifications();
    };

    const updateTradeStatus = async (tradeId, status) => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                },
            });

            const response = await fetch(`${apiUrl}/api/trades/${tradeId}/${status}`, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to ${status} trade offer`);
            }

            return true;
        } catch (error) {
            console.error(`Error updating trade status to ${status}:`, error);
            return false;
        }
    };

    const updateFriendRequest = async (Id, status) => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                },
            });

            let response = null;

            if(status === 'accept'){
                response = await fetch(`${apiUrl}/api/friends/AcceptFriendRequest/${Id}`, {
                    method: 'PUT',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
            }
            else{
                response = await fetch(`${apiUrl}/api/friends/RejectFriendRequest/${Id}`, {
                    method: 'PUT',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
            }


            if (!response.ok) {
                throw new Error(`Failed to ${status} friend request`);
            }

            return true;
        } catch (error) {
            console.error(`Error updating friend request to ${status}:`, error);
            return false;
        }
    };

    return {
        notifications,
        notificationError,
        isLoadingNotifications,
        isConnected,
        fetchNotifications,
        markAsRead,
        updateTradeStatus,
        updateFriendRequest
    };
};

export default useNotifications;
